import React, { useState } from 'react';
import { gameLabel } from '../lib/games';
import StorePhoto from './StorePhoto';

// WO-21: card image for singles of games without mirrored images (Yu-Gi-Oh first). We never hotlink TCGplayer's CDN
// (D9) and YGOPRODeck images must be downloaded once and re-hosted (SoT V7), so until a mirror exists the page draws
// a card-shaped placeholder from the card's own data. A real image_large / image_small replaces it automatically; a
// broken image falls back to the drawing.

export const GAME_TINTS = {
  yugioh: '#c9a44c',
  mtg: '#d2875a',
  lorcana: '#8b7ff0',
  onepiece: '#e06c6c',
  swu: '#7fb4e0',
  pokemon: '#6A66A3',
};

function Glyph({ c }) {
  // A plain fanned-card outline: game-neutral (no logos or trade dress).
  const s = { fill: 'none', stroke: c, strokeWidth: 2.2, strokeLinejoin: 'round', strokeLinecap: 'round' };
  const f = { fill: c, fillOpacity: 0.12, stroke: c, strokeWidth: 2.2, strokeLinejoin: 'round' };
  return (
    <svg viewBox="0 0 120 120" className="w-20 h-20" aria-hidden="true">
      <rect x="38" y="22" width="44" height="64" rx="4" {...s} transform="rotate(-10 60 54)" />
      <rect x="38" y="22" width="44" height="64" rx="4" {...f} transform="rotate(8 60 54)" />
      <path d="M52 98h16" {...s} />
    </svg>
  );
}

// WO-34: `photo` = a store listing's photo of this card (API photo_url, hotlinked from the store's Shopify CDN, never
// re-hosted), used only when we host no image; `photoCredit` is drawn on it. A load error falls back to the drawing.
const GameCardArt = ({ gameId, name, number, subtitle, image, compact = false, className = '', fit = null,
                       photo = null, photoCredit = null, onPhotoBroken = null, photoHints = null }) => {
  const [broken, setBroken] = useState(false);
  const [photoBroken, setPhotoBroken] = useState(false);
  const tint = GAME_TINTS[gameId] || GAME_TINTS.pokemon;

  if (image && !broken) {
    // WO-30: MTG images (Scryfall) are shown whole: their rules forbid cropping the artist / copyright line
    const fitClass = fit === 'contain' || (!fit && gameId === 'mtg') ? 'object-contain' : 'object-cover';
    return (
      <img src={image} alt={name} loading="lazy" className={`w-full h-full ${fitClass} ${className}`} onError={() => setBroken(true)} />
    );
  }
  if (photo && !photoBroken) {
    // WO-35: the shared store-photo frame (cream stage; the scan's white border melts in)
    return (
      <div className={`relative w-full h-full bg-[#1A1A20] ${className}`}>
        <StorePhoto url={photo} alt={name} credit={photoCredit} variant="card" testId="card-listing-photo" hints={photoHints}
                    onError={() => { setPhotoBroken(true); if (onPhotoBroken) onPhotoBroken(); }} />
      </div>
    );
  }
  if (compact) {   // listing tiles: the listing has no photo; just the outline, no repeated card name
    return (
      <div className={`w-full h-full flex items-center justify-center ${className}`}
           style={{ background: `linear-gradient(160deg, ${tint}1a 0%, #1A1A20 70%)` }} aria-hidden="true">
        <Glyph c={tint} />
      </div>
    );
  }
  return (
    <div
      className={`w-full h-full flex flex-col justify-between p-3 text-center ${className}`}
      style={{ background: `linear-gradient(160deg, ${tint}26 0%, #1A1A20 55%, ${tint}1a 100%)` }}
      role="img"
      aria-label={`${name}: image not available yet`}
      data-testid="game-card-placeholder"
    >
      <div className="rounded border px-2 py-1 text-sm font-medium text-cream leading-tight break-words" style={{ borderColor: `${tint}66` }}>
        {name}
      </div>
      <div className="flex flex-col items-center justify-center flex-grow">
        <Glyph c={tint} />
        <div className="text-[11px] text-cream/50 mt-1">Image coming soon</div>
      </div>
      <div className="rounded border px-2 py-1 text-xs text-cream/70 leading-tight" style={{ borderColor: `${tint}44` }}>
        <div className="font-mono">{number || gameLabel(gameId)}</div>
        {subtitle && <div className="text-cream/50 mt-0.5 truncate">{subtitle}</div>}
      </div>
    </div>
  );
};

export default GameCardArt;
